import { supabase } from '@/shared/services/supabase';
import { reportsService, UsageComplianceData } from '@/features/reports/services/reportsService';
import { getTodayDateString, daysBetween } from '@/shared/utils/dateUtils';
import type {
  LocationDashboardSettings,
  DashboardAlert,
  LocationDashboardSummary,
  OrderingDayInfo,
  AlertSeverity,
} from '@/shared/types/domain.types';

/**
 * Dashboard Service
 * Provides exception-based alert data for the dashboard
 */

// ============================================================================
// Type Definitions for Supabase Query Results
// ============================================================================

interface FrameReplenishmentItem {
  id: string;
  ordered_date?: string | null;
  reorder_entry_date?: string;
  location?: {
    id: string;
    name: string;
  };
}

interface LocationInfo {
  id: string;
  name: string;
  short_code: string;
}

// ============================================================================
// Default Settings (fallback if location settings not configured)
// ============================================================================

const DEFAULT_SETTINGS: Omit<LocationDashboardSettings, 'id' | 'location_id' | 'created_at' | 'updated_at'> = {
  overdue_ordered_yellow_days: 14,
  overdue_ordered_red_days: 21,
  stale_pending_yellow_days: 7,
  stale_pending_red_days: 14,
  receiving_backlog_days: 10,
  usage_yellow_pct: 70,
  usage_red_pct: 50,
  ordering_day_of_week: 4, // Thursday
  enable_overdue_ordered: true,
  enable_stale_pending: true,
  enable_receiving_backlog: true,
  enable_usage_alert: true,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get day name from day of week number
 */
function getDayName(dayOfWeek: number): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayOfWeek] || 'Unknown';
}

/**
 * Calculate days until next occurrence of target day of week
 */
function daysUntilDay(targetDayOfWeek: number): number {
  const today = new Date().getDay();
  if (today === targetDayOfWeek) {
    return 0;
  }
  let diff = targetDayOfWeek - today;
  if (diff < 0) {
    diff += 7; // Next week
  }
  return diff;
}

/**
 * Determine severity from percentage (lower is worse for usage %)
 */
function getUsageSeverity(percentage: number, yellowPct: number, redPct: number): AlertSeverity {
  if (percentage >= yellowPct) return 'green';
  if (percentage >= redPct) return 'yellow';
  return 'red';
}

// ============================================================================
// Service Implementation
// ============================================================================

export const dashboardService = {
  /**
   * Get dashboard settings for a location
   * Returns defaults if not configured
   */
  async getLocationSettings(locationId: string): Promise<LocationDashboardSettings> {
    const { data, error } = await supabase
      .from('location_dashboard_settings')
      .select('*')
      .eq('location_id', locationId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching location dashboard settings:', error);
      throw new Error(error.message);
    }

    // If no settings exist, return defaults with dummy IDs
    if (!data) {
      return {
        id: '',
        location_id: locationId,
        ...DEFAULT_SETTINGS,
        created_at: '',
        updated_at: '',
      };
    }

    return data;
  },

  /**
   * Get dashboard settings for multiple locations in one query
   * Optimized: Batch fetch instead of one query per location
   */
  async getLocationSettingsBatch(locationIds: string[]): Promise<Map<string, LocationDashboardSettings>> {
    const { data, error } = await supabase
      .from('location_dashboard_settings')
      .select('*')
      .in('location_id', locationIds);

    if (error) {
      console.error('Error fetching location dashboard settings batch:', error);
      throw new Error(error.message);
    }

    const settingsMap = new Map<string, LocationDashboardSettings>();

    // Create settings for each location (with defaults if missing)
    for (const locationId of locationIds) {
      const existing = (data as LocationDashboardSettings[] || []).find((s) => s.location_id === locationId);
      if (existing) {
        settingsMap.set(locationId, existing);
      } else {
        // Use defaults if not configured
        settingsMap.set(locationId, {
          id: '',
          location_id: locationId,
          ...DEFAULT_SETTINGS,
          created_at: '',
          updated_at: '',
        });
      }
    }

    return settingsMap;
  },

  /**
   * Get count of overdue ORDERED frames (past threshold)
   * Returns { yellow: count, red: count }
   */
  async getOverdueOrderedCount(
    locationId: string,
    settings: LocationDashboardSettings
  ): Promise<{ yellow: number; red: number; items?: FrameReplenishmentItem[] }> {
    const today = getTodayDateString();

    const { data, error } = await supabase
      .from('frame_replenishment_items')
      .select('id, ordered_date, location:locations!inner(id, name)')
      .eq('location_id', locationId)
      .eq('status', 'ORDERED')
      .not('ordered_date', 'is', null);

    if (error) {
      console.error('Error fetching overdue ordered frames:', error);
      throw new Error(error.message);
    }

    let yellowCount = 0;
    let redCount = 0;

    (data || []).forEach((item: FrameReplenishmentItem) => {
      const daysAging = daysBetween(item.ordered_date!, today);
      if (daysAging >= settings.overdue_ordered_red_days) {
        redCount++;
      } else if (daysAging >= settings.overdue_ordered_yellow_days) {
        yellowCount++;
      }
    });

    return { yellow: yellowCount, red: redCount, items: data || [] };
  },

  /**
   * Get count of stale PENDING frames (never ordered, past threshold)
   */
  async getStalePendingCount(
    locationId: string,
    settings: LocationDashboardSettings
  ): Promise<{ yellow: number; red: number; items?: FrameReplenishmentItem[] }> {
    const today = getTodayDateString();

    const { data, error } = await supabase
      .from('frame_replenishment_items')
      .select('id, reorder_entry_date, location:locations!inner(id, name)')
      .eq('location_id', locationId)
      .eq('status', 'PENDING');

    if (error) {
      console.error('Error fetching stale pending frames:', error);
      throw new Error(error.message);
    }

    let yellowCount = 0;
    let redCount = 0;

    (data || []).forEach((item: FrameReplenishmentItem) => {
      const daysAging = daysBetween(item.reorder_entry_date!, today);
      if (daysAging >= settings.stale_pending_red_days) {
        redCount++;
      } else if (daysAging >= settings.stale_pending_yellow_days) {
        yellowCount++;
      }
    });

    return { yellow: yellowCount, red: redCount, items: data || [] };
  },

  /**
   * Get count of ORDERED frames in receiving backlog
   * (Likely arrived but not marked as received)
   */
  async getReceivingBacklogCount(
    locationId: string,
    settings: LocationDashboardSettings
  ): Promise<number> {
    const today = getTodayDateString();

    const { data, error } = await supabase
      .from('frame_replenishment_items')
      .select('id, ordered_date')
      .eq('location_id', locationId)
      .eq('status', 'ORDERED')
      .not('ordered_date', 'is', null);

    if (error) {
      console.error('Error fetching receiving backlog:', error);
      throw new Error(error.message);
    }

    let backlogCount = 0;
    (data || []).forEach((item: FrameReplenishmentItem) => {
      const daysAging = daysBetween(item.ordered_date!, today);
      if (daysAging >= settings.receiving_backlog_days) {
        backlogCount++;
      }
    });

    return backlogCount;
  },

  /**
   * Get ordering day info for a location
   */
  getOrderingDayInfo(locationId: string, locationName: string, locationShortCode: string, settings: LocationDashboardSettings): OrderingDayInfo {
    const today = new Date().getDay();
    const targetDay = settings.ordering_day_of_week;
    const isToday = today === targetDay;
    const daysUntil = daysUntilDay(targetDay);

    return {
      isToday,
      daysUntil,
      dayName: getDayName(targetDay),
      location_id: locationId,
      location_name: locationName,
      location_short_code: locationShortCode,
    };
  },

  /**
   * Get usage percentage for a location (last 30 working days)
   * Reuses reports service logic
   */
  async getLocationUsage(locationId: string): Promise<UsageComplianceData | null> {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const dateFrom = thirtyDaysAgo.toISOString().split('T')[0];
    const dateTo = today.toISOString().split('T')[0];

    try {
      const usageData = await reportsService.getUsageCompliance({
        date_from: dateFrom,
        date_to: dateTo,
        location_ids: [locationId],
      });

      return usageData.find((u) => u.location_id === locationId) || null;
    } catch (error) {
      console.error('Error fetching location usage:', error);
      return null;
    }
  },

  /**
   * Get all dashboard alerts for given location IDs
   * Returns only yellow/red alerts (exception-based)
   * Optimized: Parallel queries instead of sequential
   */
  async getDashboardAlerts(locationIds: string[]): Promise<DashboardAlert[]> {
    if (!locationIds || locationIds.length === 0) {
      return [];
    }

    const startTime = performance.now();
    // Fetch all locations and settings in parallel
    const [locationsResult, settingsMap] = await Promise.all([
      supabase
        .from('locations')
        .select('id, name, short_code')
        .in('id', locationIds),
      this.getLocationSettingsBatch(locationIds),
    ]);

    if (locationsResult.error) {
      console.error('Error fetching locations:', locationsResult.error);
      throw new Error(locationsResult.error.message);
    }

    const locationMap = new Map((locationsResult.data || []).map((l: LocationInfo) => [l.id, { name: l.name, short_code: l.short_code }]));

    // Process all locations in parallel
    const alertPromises = locationIds.map(async (locationId) => {
      const location = locationMap.get(locationId) || { name: 'Unknown', short_code: '' };
      const locationName = location.name;
      const locationShortCode = location.short_code;
      const settings = settingsMap.get(locationId)!;

      const locationAlerts: DashboardAlert[] = [];

      // Run all queries for this location in parallel
      const [overdueResult, stalePendingResult, backlogCount, usage] = await Promise.all([
        settings.enable_overdue_ordered
          ? this.getOverdueOrderedCount(locationId, settings)
          : Promise.resolve({ yellow: 0, red: 0 }),
        settings.enable_stale_pending
          ? this.getStalePendingCount(locationId, settings)
          : Promise.resolve({ yellow: 0, red: 0 }),
        settings.enable_receiving_backlog
          ? this.getReceivingBacklogCount(locationId, settings)
          : Promise.resolve(0),
        settings.enable_usage_alert ? this.getLocationUsage(locationId) : Promise.resolve(null),
      ]);

      // 1. Overdue ORDERED frames
      if (settings.enable_overdue_ordered) {
        const totalOverdue = overdueResult.yellow + overdueResult.red;
        if (totalOverdue > 0) {
          const severity: AlertSeverity = overdueResult.red > 0 ? 'red' : 'yellow';
          locationAlerts.push({
            type: 'OVERDUE_ORDERED',
            location_id: locationId,
            location_name: locationName,
            location_short_code: locationShortCode,
            severity,
            count: totalOverdue,
            message: `${totalOverdue} ordered frame${totalOverdue > 1 ? 's' : ''} overdue`,
            enabled: true,
            target_page: 'receiving',
          });
        }
      }

      // 2. Stale PENDING frames
      if (settings.enable_stale_pending) {
        const totalStale = stalePendingResult.yellow + stalePendingResult.red;
        if (totalStale > 0) {
          const severity: AlertSeverity = stalePendingResult.red > 0 ? 'red' : 'yellow';
          locationAlerts.push({
            type: 'STALE_PENDING',
            location_id: locationId,
            location_name: locationName,
            location_short_code: locationShortCode,
            severity,
            count: totalStale,
            message: `${totalStale} frame${totalStale > 1 ? 's' : ''} pending too long`,
            enabled: true,
            target_page: 'weekly-ordering',
          });
        }
      }

      // 3. Receiving backlog
      if (settings.enable_receiving_backlog && backlogCount > 0) {
        locationAlerts.push({
          type: 'RECEIVING_BACKLOG',
          location_id: locationId,
          location_name: locationName,
          location_short_code: locationShortCode,
          severity: 'yellow',
          count: backlogCount,
          message: `${backlogCount} frame${backlogCount > 1 ? 's' : ''} likely received but not marked`,
          enabled: true,
          target_page: 'receiving',
        });
      }

      // 4. Ordering day alert
      const orderingInfo = this.getOrderingDayInfo(locationId, locationName, locationShortCode, settings);
      if (orderingInfo.isToday) {
        locationAlerts.push({
          type: 'ORDERING_DAY',
          location_id: locationId,
          location_name: locationName,
          location_short_code: locationShortCode,
          severity: 'info',
          count: 0,
          message: `Today is your ordering day (${orderingInfo.dayName})`,
          enabled: true,
          target_page: 'weekly-ordering',
        });
      }

      // 5. Low usage (lower priority, only if below thresholds)
      if (settings.enable_usage_alert && usage && usage.usage_percentage < settings.usage_yellow_pct) {
        const severity = getUsageSeverity(
          usage.usage_percentage,
          settings.usage_yellow_pct,
          settings.usage_red_pct
        );

        // Only show if yellow or red
        if (severity !== 'green') {
          locationAlerts.push({
            type: 'LOW_USAGE',
            location_id: locationId,
            location_name: locationName,
            location_short_code: locationShortCode,
            severity,
            count: Math.round(usage.usage_percentage),
            message: 'Low entry activity — frames have not been entered regularly during the last 30 working days.',
            enabled: true,
            target_page: 'reports',
          });
        }
      }

      return locationAlerts;
    });

    // Wait for all locations to process in parallel
    const allAlerts = await Promise.all(alertPromises);
    const duration = performance.now() - startTime;
    console.log(`⏱️ getDashboardAlerts (${locationIds.length} locations, optimized: parallel): ${duration.toFixed(2)}ms`);
    return allAlerts.flat();
  },

  /**
   * Get multi-location summary for regional managers
   * Returns table data with key metrics per location
   * Optimized: Parallel queries instead of sequential
   */
  async getMultiLocationSummary(locationIds: string[]): Promise<LocationDashboardSummary[]> {
    if (!locationIds || locationIds.length === 0) {
      return [];
    }

    const startTime = performance.now();
    // Fetch all locations and settings in parallel
    const [locationsResult, settingsMap] = await Promise.all([
      supabase
        .from('locations')
        .select('id, name, short_code')
        .in('id', locationIds)
        .eq('is_active', true),
      this.getLocationSettingsBatch(locationIds),
    ]);

    if (locationsResult.error) {
      console.error('Error fetching locations:', locationsResult.error);
      throw new Error(locationsResult.error.message);
    }

    // Process all locations in parallel
    const summaryPromises = (locationsResult.data || []).map(async (location: { id: string; name: string; short_code: string }) => {
      const settings = settingsMap.get(location.id)!;

      // Get all metrics in parallel for this location
      const [overdueResult, stalePendingResult, backlogCount, usage] = await Promise.all([
        this.getOverdueOrderedCount(location.id, settings),
        this.getStalePendingCount(location.id, settings),
        this.getReceivingBacklogCount(location.id, settings),
        this.getLocationUsage(location.id),
      ]);

      const orderingInfo = this.getOrderingDayInfo(location.id, location.name, location.short_code, settings);
      const overdueCount = overdueResult.yellow + overdueResult.red;
      const stalePendingCount = stalePendingResult.yellow + stalePendingResult.red;
      const usagePercentage = usage?.usage_percentage || 0;

      return {
        location_id: location.id,
        location_name: location.name,
        location_short_code: location.short_code,
        usage_percentage: Math.round(usagePercentage),
        usage_severity: getUsageSeverity(usagePercentage, settings.usage_yellow_pct, settings.usage_red_pct),
        overdue_ordered_count: overdueCount,
        overdue_ordered_severity: (overdueResult.red > 0 ? 'red' : overdueResult.yellow > 0 ? 'yellow' : 'green') as AlertSeverity,
        stale_pending_count: stalePendingCount,
        stale_pending_severity: (stalePendingResult.red > 0 ? 'red' : stalePendingResult.yellow > 0 ? 'yellow' : 'green') as AlertSeverity,
        receiving_backlog_count: backlogCount,
        is_ordering_day: orderingInfo.isToday,
      };
    });

    const summaries = await Promise.all(summaryPromises);
    const duration = performance.now() - startTime;
    console.log(`⏱️ getMultiLocationSummary (${locationIds.length} locations, optimized: parallel): ${duration.toFixed(2)}ms`);
    return summaries.sort((a, b) => a.location_name.localeCompare(b.location_name));
  },
};
